import * as THREE from 'three'
import { SkeletonUtils } from 'three-stdlib'

export function createPivotAnchored(
  object: THREE.Object3D,
  {
    anchorY = 'min',
    anchorZ = 'min',
  }: {
    anchorY?: 'min' | 'center' | 'max'
    anchorZ?: 'min' | 'center' | 'max'
  } = {}
) {
  const pivot = new THREE.Group()
  pivot.add(object)

  const box = new THREE.Box3().setFromObject(object)
  const getAnchor = (
    min: number,
    max: number,
    kind: 'min' | 'center' | 'max'
  ) => (kind === 'min' ? min : kind === 'max' ? max : (min + max) * 0.5)

  const ay = getAnchor(box.min.y, box.max.y, anchorY)
  const az = getAnchor(box.min.z, box.max.z, anchorZ)

  object.position.y -= ay
  object.position.z -= az

  pivot.updateMatrixWorld(true)
  return pivot
}

// Clone an Object3D tree, deeply cloning geometry + materials so disposal
// on the clone doesn't free GPU buffers held by the shared GLTF source
// cache.
export const cloneWithIndependentMaterials = (
  sourceObject: THREE.Object3D,
  {
    castShadow = true,
    receiveShadow = true,
  }: { castShadow?: boolean; receiveShadow?: boolean } = {}
): THREE.Object3D => {
  const clonedRoot = SkeletonUtils.clone(sourceObject)
  clonedRoot.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return
    const meshNode = node
    if (meshNode.geometry) {
      meshNode.geometry = meshNode.geometry.clone()
    }
    const originalMaterial = meshNode.material
    if (originalMaterial) {
      meshNode.material = Array.isArray(originalMaterial)
        ? originalMaterial.map((materialInstance) => materialInstance.clone())
        : originalMaterial.clone()
    }
    meshNode.castShadow = castShadow
    meshNode.receiveShadow = receiveShadow
  })
  return clonedRoot
}

export const createPanelPivotWithFlag = (
  sourceObject: THREE.Object3D,
  panelFlagKey: string,
  anchor: {
    anchorY?: 'min' | 'center' | 'max'
    anchorZ?: 'min' | 'center' | 'max'
  } = { anchorY: 'min', anchorZ: 'min' }
): THREE.Object3D => {
  const panelModel = cloneWithIndependentMaterials(sourceObject)
  const panelPivot = createPivotAnchored(panelModel, anchor)
  panelPivot.userData[panelFlagKey] = true
  return panelPivot
}
